import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import type { DraftRequest } from 'app/components/inbox/scheduling-dock/types'

// The import above is type-only, so it is erased at compile time:
// `contacts/inbox` gains no runtime dependency on `inbox/scheduling-dock`,
// which matters because `ContactInbox` also renders in the contact drawer
// where no dock exists.

/**
 * Writes an externally supplied draft into the composer. Keyed on `id`, not on
 * `text`, so booking twice with identical wording still writes a second draft.
 *
 * Appends rather than replaces: an automatic write must never destroy something
 * the staff member was in the middle of typing.
 *
 * If the composer is focused with the caret mid-text, the DOM write moves the
 * caret to the end (the Textarea is controlled, so React rewrites its value).
 * No keystrokes are lost since the update is functional and never reads a
 * stale closure, but the caret moves. Accepted: restoring selectionStart /
 * selectionEnd by hand isn't worth it for a write that happens at most once
 * per booking.
 */
export function useDraftRequest(
  request: DraftRequest | undefined,
  setDraftText: Dispatch<SetStateAction<string>>,
): void {
  const lastIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!request?.text) return
    if (lastIdRef.current === request.id) return
    lastIdRef.current = request.id
    setDraftText((prev) => (prev.trim().length > 0 ? `${prev}\n${request.text}` : request.text))
  }, [request, setDraftText])
}
